import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { WrenchIcon, SparklesIcon, PaintbrushIcon, ShieldCheckIcon, BeakerIcon, DropletIcon, SprayCanIcon, StarIcon, PackageIcon, BoxIcon, CarIcon, FlaskConicalIcon, ArrowRightIcon } from 'lucide-react';
type Language = 'ua' | 'en';
type ProductLinesProps = {
  language: Language;
};
const categories = {
  ua: [{
    icon: WrenchIcon,
    name: 'Шпаклівка'
  }, {
    icon: SparklesIcon,
    name: 'Лаки'
  }, {
    icon: PaintbrushIcon,
    name: 'Грунти'
  }, {
    icon: ShieldCheckIcon,
    name: 'Захисні покриття'
  }, {
    icon: BeakerIcon,
    name: 'Ready Mix'
  }, {
    icon: FlaskConicalIcon,
    name: 'Розчинники та знежирювачі'
  }, {
    icon: SprayCanIcon,
    name: 'Аерозолі'
  }, {
    icon: StarIcon,
    name: 'Поліролі'
  }, {
    icon: DropletIcon,
    name: 'Клеї та герметики'
  }, {
    icon: BoxIcon,
    name: 'Витратні матеріали'
  }, {
    icon: ShieldCheckIcon,
    name: 'CHA Protect'
  }, {
    icon: CarIcon,
    name: 'Автокосметика'
  }],
  en: [{
    icon: WrenchIcon,
    name: 'Putty'
  }, {
    icon: SparklesIcon,
    name: 'Lacquers'
  }, {
    icon: PaintbrushIcon,
    name: 'Primers'
  }, {
    icon: ShieldCheckIcon,
    name: 'Protective Coatings'
  }, {
    icon: BeakerIcon,
    name: 'Ready Mix'
  }, {
    icon: FlaskConicalIcon,
    name: 'Solvents & Degreasers'
  }, {
    icon: SprayCanIcon,
    name: 'Aerosols'
  }, {
    icon: StarIcon,
    name: 'Polishes'
  }, {
    icon: DropletIcon,
    name: 'Adhesives & Sealants'
  }, {
    icon: BoxIcon,
    name: 'Consumables'
  }, {
    icon: ShieldCheckIcon,
    name: 'CHA Protect'
  }, {
    icon: CarIcon,
    name: 'Auto Cosmetics'
  }]
};
const sectionText = {
  ua: {
    title: 'Каталог продукції',
    subtitle: 'Професійні рішення для автомобільної індустрії',
    viewCategory: 'Переглянути'
  },
  en: {
    title: 'Product Catalog',
    subtitle: 'Professional solutions for automotive industry',
    viewCategory: 'View'
  }
};
export function ProductLines({
  language
}: ProductLinesProps) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const t = sectionText[language];
  const categoryList = categories[language];
  return <section ref={ref} id="products" className="relative py-16 sm:py-20 lg:py-32 bg-chamaleon-black overflow-hidden" data-id="element-283">
      {/* Subtle background */}
      <div className="absolute inset-0" data-id="element-284">
        <div className="absolute inset-0 opacity-[0.015]" style={{
        backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                           linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
        backgroundSize: '60px 60px'
      }} data-id="element-285" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-286">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} className="text-center mb-12 lg:mb-16" data-id="element-287">
          <h2 className="text-3xl sm:text-4xl lg:text-6xl font-bold text-white mb-3 sm:mb-4" data-id="element-288">
            {t.title}
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-gray-400 max-w-2xl mx-auto" data-id="element-289">
            {t.subtitle}
          </p>
          <div className="w-24 h-1 bg-gradient-to-r from-transparent via-chamaleon-orange to-transparent mx-auto mt-6" data-id="element-290" />
        </motion.div>

        {/* Categories Grid - Unified style with Bestsellers */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6" data-id="element-291">
          {categoryList.map((category, index) => {
          const Icon = category.icon;
          return <motion.a key={category.name} href="#" initial={{
            opacity: 0,
            y: 30
          }} animate={isInView ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            duration: 0.5,
            delay: index * 0.05
          }} whileHover={{
            y: -8
          }} className="group block" data-id="element-292">
                {/* Card with unified style - matches Bestsellers */}
                <div className="glass-card rounded-xl sm:rounded-2xl overflow-hidden h-full transition-all duration-300 hover:border-chamaleon-orange/30" data-id="element-293">
                  {/* Icon area */}
                  <div className="relative h-40 sm:h-48 bg-gradient-to-br from-chamaleon-charcoal to-chamaleon-black overflow-hidden flex items-center justify-center" data-id="element-294">
                    {/* Icon */}
                    <motion.div whileHover={{
                  scale: 1.1,
                  rotate: 5
                }} transition={{
                  duration: 0.3
                }} className="relative" data-id="element-295">
                      <Icon className="w-16 h-16 sm:w-20 sm:h-20 text-white/80 group-hover:text-chamaleon-orange transition-colors duration-300" strokeWidth={1.5} data-id="element-296" />
                    </motion.div>

                    {/* Hover glow */}
                    <div className="absolute inset-0 bg-gradient-to-t from-chamaleon-orange/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-297" />
                  </div>

                  {/* Content */}
                  <div className="p-4 sm:p-6" data-id="element-298">
                    {/* Category name */}
                    <h3 className="text-lg sm:text-xl font-semibold text-white mb-4 group-hover:text-chamaleon-orange transition-colors min-h-[3.5rem] flex items-center" data-id="element-299">
                      {category.name}
                    </h3>

                    {/* CTA */}
                    <motion.div className="inline-flex items-center gap-2 text-chamaleon-orange font-medium text-sm group/link" whileHover={{
                  x: 5
                }} data-id="element-300">
                      {t.viewCategory}
                      <ArrowRightIcon className="w-4 h-4 group-hover/link:translate-x-1 transition-transform" data-id="element-301" />
                    </motion.div>
                  </div>
                </div>
              </motion.a>;
        })}
        </div>
      </div>
    </section>;
}